import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { postsAPI } from '../services/api'
import { Post } from '../types'
import VideoPlayer from '../components/VideoPlayer'
import AudioPlayer from '../components/AudioPlayer'
import './Home.css'

const Home: React.FC = () => {
  const [featuredPosts, setFeaturedPosts] = useState<Post[]>([])
  const [latestPosts, setLatestPosts] = useState<Post[]>([])
  const [videoPosts, setVideoPosts] = useState<Post[]>([])
  const [podcastPosts, setPodcastPosts] = useState<Post[]>([])
  const [mostViewedPosts, setMostViewedPosts] = useState<Post[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    loadHomeData()
  }, [])

  const loadHomeData = async () => {
    try {
      // Load all sections data
      const [featured, latest, videos, podcasts, mostViewed] = await Promise.all([
        postsAPI.getFeatured(6),
        postsAPI.getAll({ limit: 12 }),
        postsAPI.getByMediaType('video', 6),
        postsAPI.getByMediaType('podcast', 6),
        postsAPI.getMostViewed(10)
      ])
      
      setFeaturedPosts(featured.posts || [])
      setLatestPosts(latest.posts || [])
      setVideoPosts(videos.posts || [])
      setPodcastPosts(podcasts.posts || [])
      setMostViewedPosts(mostViewed.posts || [])
    } catch (error) {
      console.error('Failed to load home data:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading">Đang tải...</div>
      </div>
    )
  }

  return (
    <div className="home">
      {/* Breaking News Section */}
      <section className="breaking-news">
        <div className="container">
          <div className="breaking-label">Tin nổi bật</div>
          <div className="breaking-content">
            {featuredPosts.slice(0, 1).map(post => (
              <Link key={post.id} to={`/post/${post.slug}`} className="breaking-link">
                {post.title}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <div className="container">
        <div className="home-content">
          {/* Featured Articles */}
          {featuredPosts.length > 0 && (
            <section className="featured-section">
              <h2 className="section-title">Tin tức nổi bật</h2>
              <div className="featured-grid">
                {featuredPosts.slice(0, 6).map(post => (
                  <article key={post.id} className="featured-article">
                    {post.featured_image && (
                      <div className="article-image">
                        <img src={post.featured_image} alt={post.title} />
                      </div>
                    )}
                    <div className="article-content">
                      <div className="article-meta">
                        <span className="category">{post.category_name}</span>
                        <span className="date">{formatDate(post.publish_date)}</span>
                      </div>
                      <h3 className="article-title">
                        <Link to={`/post/${post.slug}`}>{post.title}</Link>
                      </h3>
                      {post.excerpt && (
                        <p className="article-excerpt">{post.excerpt}</p>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            </section>
          )}

          {/* Video-Clips Section */}
          {videoPosts.length > 0 && (
            <section className="video-section">
              <div className="section-header">
                <h2 className="section-title">Video - Clips</h2>
                <Link to="/category/video" className="view-more">Xem thêm →</Link>
              </div>
              <div className="video-grid">
                <div className="main-video">
                  {videoPosts[0] && (
                    <article className="video-main-article">
                      <VideoPlayer
                        src={videoPosts[0].media_url || ''}
                        poster={videoPosts[0].featured_image}
                        title={videoPosts[0].title}
                        className="compact"
                      />
                      <div className="video-content">
                        <div className="video-meta">
                          <span className="video-date">{formatDate(videoPosts[0].publish_date)}</span>
                          <span className="video-duration">
                            {videoPosts[0].media_duration ? Math.floor(videoPosts[0].media_duration / 60) : 0}:{(videoPosts[0].media_duration || 0) % 60 < 10 ? '0' : ''}{(videoPosts[0].media_duration || 0) % 60}
                          </span>
                        </div>
                        <h3 className="video-title">
                          <Link to={`/post/${videoPosts[0].slug}`}>{videoPosts[0].title}</Link>
                        </h3>
                      </div>
                    </article>
                  )}
                </div>
                <div className="video-sidebar">
                  {videoPosts.slice(1, 4).map(post => (
                    <article key={post.id} className="video-side-article">
                      <div className="video-thumbnail">
                        {post.featured_image && (
                          <img src={post.featured_image} alt={post.title} />
                        )}
                        <div className="play-overlay">▶</div>
                        <div className="duration-badge">
                          {post.media_duration ? Math.floor(post.media_duration / 60) : 0}:{(post.media_duration || 0) % 60 < 10 ? '0' : ''}{(post.media_duration || 0) % 60}
                        </div>
                      </div>
                      <div className="video-info">
                        <h4 className="video-title">
                          <Link to={`/post/${post.slug}`}>{post.title}</Link>
                        </h4>
                        <div className="video-meta">
                          <span className="video-date">{formatDate(post.publish_date)}</span>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Podcast Section */}
          {podcastPosts.length > 0 && (
            <section className="podcast-section">
              <div className="section-header">
                <h2 className="section-title">Podcast</h2>
                <Link to="/category/podcast" className="view-more">Xem thêm →</Link>
              </div>
              <div className="podcast-grid">
                {podcastPosts.slice(0, 3).map(post => (
                  <article key={post.id} className="podcast-article">
                    <AudioPlayer
                      src={post.media_url || ''}
                      title={post.title}
                      artwork={post.featured_image}
                      className="compact"
                    />
                    <div className="podcast-content">
                      <div className="podcast-meta">
                        <span className="podcast-date">{formatDate(post.publish_date)}</span>
                        <span className="podcast-duration">
                          {post.media_duration ? Math.floor(post.media_duration / 60) : 0}:{(post.media_duration || 0) % 60 < 10 ? '0' : ''}{(post.media_duration || 0) % 60}
                        </span>
                      </div>
                      <h3 className="podcast-title">
                        <Link to={`/post/${post.slug}`}>{post.title}</Link>
                      </h3>
                      {post.excerpt && (
                        <p className="podcast-excerpt">{post.excerpt}</p>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            </section>
          )}

          {/* Most Viewed Section */}
          {mostViewedPosts.length > 0 && (
            <section className="most-viewed-section">
              <h2 className="section-title">Tin xem nhiều</h2>
              <div className="most-viewed-grid">
                {mostViewedPosts.slice(0, 6).map((post, index) => (
                  <article key={post.id} className="most-viewed-article">
                    <div className="article-content">
                      <div className="article-meta">
                        <span className="category">{post.category_name}</span>
                        <span className="date">{formatDate(post.publish_date)}</span>
                      </div>
                      <h3 className="article-title">
                        <Link to={`/post/${post.slug}`}>{post.title}</Link>
                      </h3>
                      <div className="article-stats">
                        <span className="views-highlight">👁 {post.view_count} lượt xem</span>
                        {post.author_name && (
                          <span className="author">Tác giả: {post.author_name}</span>
                        )}
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          )}

          {/* Latest News */}
          <section className="latest-section">
            <h2 className="section-title">Tin tức mới nhất</h2>
            <div className="latest-grid">
              {latestPosts.map(post => (
                <article key={post.id} className="latest-article">
                  <div className="article-meta">
                    <span className="category">{post.category_name}</span>
                    <span className="date">{formatDate(post.publish_date)}</span>
                  </div>
                  <h3 className="article-title">
                    <Link to={`/post/${post.slug}`}>{post.title}</Link>
                  </h3>
                  {post.excerpt && (
                    <p className="article-excerpt">{post.excerpt}</p>
                  )}
                  <div className="article-footer">
                    <span className="author">Tác giả: {post.author_name}</span>
                    <span className="views">{post.view_count} lượt xem</span>
                  </div>
                </article>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}

export default Home